import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const {FiTrendingUp, FiHome, FiDollarSign, FiBarChart2, FiSearch, FiAward, FiTarget, FiPieChart} = FiIcons;

const RealEstateInvestmentPage = () => {
  const investmentServices = [
    {
      icon: FiSearch,
      title: 'Property Acquisition',
      description: 'Expert property identification and acquisition services with thorough due diligence and market analysis.',
      features: ['Market analysis', 'Property evaluation', 'Investment opportunity identification', 'Due diligence support']
    },
    {
      icon: FiBarChart2,
      title: 'Investment Strategy',
      description: 'Customized investment strategies tailored to your financial goals and risk tolerance.',
      features: ['Portfolio optimization', 'Risk assessment', 'ROI projections', 'Investment timeline planning']
    },
    {
      icon: FiDollarSign,
      title: 'Financial Analysis',
      description: 'Comprehensive financial modeling and analysis to maximize investment returns.',
      features: ['Cash flow projections', 'Cap rate analysis', 'Market trend analysis', 'Investment benchmarking']
    },
    {
      icon: FiHome,
      title: 'Portfolio Management',
      description: 'Ongoing portfolio oversight and optimization to achieve long-term investment success.',
      features: ['Performance monitoring', 'Asset allocation', 'Strategic adjustments', 'Growth planning']
    }
  ];

  const benefits = [
    '15+ Years Calgary Market Experience',
    'Data-Driven Investment Decisions',
    'Comprehensive Market Analysis',
    'ROI Optimization Strategies',
    'Risk Mitigation Planning',
    'Long-Term Wealth Building'
  ];

  const marketInsights = [
    'Calgary Rental Market Trends',
    'Neighborhood Growth Analysis',
    'Investment Hotspots Identification',
    'Property Value Appreciation',
    'Rental Yield Optimization',
    'Economic Impact Assessment'
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Real Estate Investment Services Calgary",
    "description": "Professional real estate investment services in Calgary including property acquisition, investment strategy, financial analysis, and portfolio management for optimal returns.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Real Estate Investment", "Property Acquisition", "Investment Strategy", "Financial Analysis", "Portfolio Management"]
  };

  return (
    <>
      <SEOHead
        title="Real Estate Investment Services Calgary | Property Investment Strategy"
        description="DGM Property Management offers expert real estate investment services in Calgary. Property acquisition, investment strategy, financial analysis, and portfolio management. 15+ years experience. Call 587-800-1215."
        keywords="real estate investment Calgary, Calgary investment properties, property investment strategy Calgary, real estate portfolio management, Calgary property acquisition, investment analysis Calgary"
        canonical="/services/real-estate-investment"
        ogImage="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Real Estate Investment Services Calgary
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Expert investment guidance and property acquisition services with 15+ years of Calgary market experience. 
                Build wealth through strategic real estate investments with data-driven decision making.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Investment Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Investment Solutions</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From property acquisition to portfolio management, we provide end-to-end investment services for Calgary real estate.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {investmentServices.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiTarget} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Market Insights Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Calgary Market Insights</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Deep market knowledge and data-driven insights to inform your investment decisions.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {marketInsights.map((insight, index) => (
                <motion.div
                  key={insight}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 text-center shadow-lg"
                >
                  <SafeIcon icon={FiPieChart} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900">{insight}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Choose Our Investment Services?</h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Real estate investment and property portfolio management"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Investment Strategy Section */}
        <section className="py-20 bg-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <h2 className="text-4xl font-bold text-gray-900">Build Your Real Estate Portfolio</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Our investment strategies have helped clients achieve an average of 18% annual ROI through strategic Calgary property investments.
                </p>
                <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">500+</div>
                    <p className="text-gray-700">Properties Managed</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">18%</div>
                    <p className="text-gray-700">Average Annual ROI</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">15+</div>
                    <p className="text-gray-700">Years Experience</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Start Building Your Real Estate Wealth</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Our expert investment guidance and market knowledge can help you achieve your financial goals through strategic Calgary real estate investments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Schedule Investment Consultation
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RealEstateInvestmentPage;